/***
 * Created on 2025/4/29
 * @author: Adam Cheng <adamcheng@qnap.com>
 ***/

import React, {useState} from 'react';
import {useTranslation} from "react-i18next";
import SelectInputComponent from "../common/Form/SelectInputComponent";
import CloudSpaceLimitUserList from "./CloudSpaceLimitUserList";
import {normalizeSpaceSize} from "../../service/utils";
import {useMutation, useQueryClient} from "@tanstack/react-query";
import {useOrganizationApi} from "../../hook/OrganizationAPIHook";
import {useOrganization} from "../../context/OrganizationContext";
import {invalidateAccessGrant} from "../../service/accessGrantSync";
import ModalAlertComponent from "../common/ModalComonent/ModalAlertComponent";
import {useAlertContext} from "../../context/AlertContext";
import CloudSpaceUserSpaceSharingComponent from "./CloudSpaceUserSpaceSharingComponent";


const SIZE_MAPPING = {
    '200GB': 214748364800, //200 * 1024 * 1024 * 1024
    '500GB': 536870912000, //500 * 1024 * 1024 * 1024
    '1TB': 1099511627776   //1 * 1024 * 1024 * 1024 * 1024
}

function CloudSpaceUserSpaceLimitComponent({usage_data, isReadOnly = false}) {
    const { t } = useTranslation();
    const [alertType, setAlertType] = useState(null);
    const alert = useAlertContext();
    const {OrganizationAPI} = useOrganizationApi();
    const {getCurrentOrg} = useOrganization();
    const currentUser = JSON.parse(window.localStorage.getItem('currentUser'));
    const queryClient = useQueryClient();
    const org = getCurrentOrg();
    const [input, setInput] = useState({
        size: {value: Object.entries(SIZE_MAPPING).find(([k,v]) => v === usage_data?.personal_max_size)?.[0]  || null, error: null, required: false}
    });

    const DEFAULT_SIZE_LIST = [
        t('@Unrestricted@'),
        '200GB',
        '500GB',
        '1TB'
    ];

    const updateUserLimitMutate = useMutation({
        mutationFn: async (data) => await OrganizationAPI.q2.put(`org_storage/${org.organization_id}/space_limit`, {}, data),
        onSuccess: async (data, variables) => {
            console.log('Update user limit success', data, variables, usage_data);
            const activity_data = {
                organization_id: org.organization_id,
                user_id: currentUser.user_id,
                event: 24,
                category: 6,
                meta_data: {data: {...variables, previous_personal_max_size: usage_data?.personal_max_size}}
            }
            await OrganizationAPI.put('organization_activity',null, activity_data);
            invalidateAccessGrant(queryClient, org.organization_id);
            setAlertType(null);
            alert(true, 'success', 0, t('@Personal folder quota updated successfully.@'), 10000);
        },
        onError: (error) => {
            console.log('Update user limit error', error);
            alert(true, 'error',error?.code, 'Failed to update quota. Please check the current usage and try again.');
        }
    });

    const updateUserLimit = async () => {
        const size = SIZE_MAPPING[input.size.value] || 0;
        const exception_user_data = usage_data?.exception_user_ids?.map(u => ({id: u.id, qid: u.email})) || [];
        const exception_user_ids = exception_user_data?.map(u => u.id) || [];
        const payload = {
            personal_max_size: size,
            ignore_warning: true,
            exception_user_ids,
            exception_user_data
        }
        await updateUserLimitMutate.mutateAsync(payload);
    }

    const onCancelAlert = () => {
        setInput((prevInput) => {
            prevInput['size'] = {
                value: normalizeSpaceSize(usage_data?.personal_max_size) || null,
                error: null,
                required: false
            }
            return {...prevInput}
        });
        setAlertType(null);
    }

    const onLimitChange = async (value) => {
        if (value === t('@Unrestricted@')) {
            setAlertType('remove');
            return;
        }
        if (usage_data?.personal_max_size > SIZE_MAPPING[value]) {
            setAlertType('reduce');
            return;
        }
        await updateUserLimit();
    }

    return (
        <>
            <div className="section-title-bar">
                <div className="col-label">{t('@User Space Limit@')}</div>
            </div>
            <div className="section-content indent-l-15">
                <div className="section-content__desc">
                    {t('@Limit the amount of space each member can use in their personal folder within each region.@')}
                </div>
                <div className="storage-limit-setting">
                    <div>{t('@Personal Folder Storage Limit:@')}</div>
                    <SelectInputComponent field_name={'size'} input={input} setInput={setInput} listData={DEFAULT_SIZE_LIST} useLocale={false} empty_text={t('@Unrestricted@')} onChange={v=>onLimitChange(v)} disabled={isReadOnly} />
                </div>

                {usage_data?.personal_max_size && <CloudSpaceLimitUserList usage_data={usage_data} isReadOnly={isReadOnly} />}
            </div>

            {!["true", true].includes(window._env_.REACT_APP_HIDE_SHARE_SPACE) && <CloudSpaceUserSpaceSharingComponent profile={usage_data} isReadOnly={isReadOnly}/>}

            { alertType === 'reduce' &&
                <ModalAlertComponent isOpen={alertType==='reduce'} onClose={onCancelAlert} okText={t('@Confirm@')} cancelText={t('@Cancel@')} onConfirmAction={updateUserLimit}>
                    <div className="alert-msg-inner-head">{t('@Reduce Personal Folder Quota@')}</div>
                    <p>
                        {t('@Changing the quota from {{original_limit_unit}} to {{new_limit_unit}} may immediately affect members whose current usage exceeds the new limit.@', {
                            original_limit_unit: normalizeSpaceSize(usage_data?.personal_max_size),
                            new_limit_unit: input.size.value
                        })}
                    </p>
                    <p>
                        {t('@These members will temporarily lose upload and sync permissions for their Personal Folder until their usage drops below the new limit.@')}
                    </p>
                    <p>
                        {t('@Are you sure you want to proceed?@')}
                    </p>
                </ModalAlertComponent>
            }

            { alertType === 'remove' &&
                <ModalAlertComponent isOpen={alertType==='remove'} onClose={onCancelAlert} okText={t('@Confirm@')} cancelText={t('@Cancel@')} onConfirmAction={updateUserLimit}>
                    <div className="alert-msg-inner-head">{t('@Remove Personal Folder Quota@')}</div>
                    <p>
                        {t('@Switching to an unrestricted quota will allow all organization members to upload files without personal limits, until the total Organization Space is full.@')}
                    </p>
                    <p>
                        {t('@Any existing whitelist settings will be temporarily disabled but saved for future use if a quota is re-applied.@')}
                    </p>
                    <p>
                        {t('@Are you sure you want to proceed?@')}
                    </p>
                </ModalAlertComponent>
            }
        </>
    );
}

export default CloudSpaceUserSpaceLimitComponent;